import { html } from '@polymer/polymer/polymer-element.js';
import { KatapultWidget } from './katapult-widget';
import '../../style-modules/paper-table-style';
import { Path } from '../../../modules/Path.js';
import { CamelCase } from '../../../modules/CamelCase.js';
import '../../katapult-elements/katapult-button.js';

/**
 * `katapult-project-billable-widget`
 *
 *
 * @customElement
 * @polymer
 * @demo demo/index.html
 */
class KatapultProjectBillableWidget extends KatapultWidget {
  static get is() {
    return 'katapult-project-billable-widget';
  }

  static get dataBaseTemplate() {
    return html`
      <katapult-firebase-worker id="projects" path="photoheight/company_space/[[userGroup]]/project_tracking/projects" data="{{projects}}">
      </katapult-firebase-worker>
      <template is="dom-if" if="[[units.length]]"> </template>
    `;
  }
  static get widgetTitleTemplate() {
    return html`Billable Units`;
  }
  static get primaryControlTemplate() {
    return html`
      <style>
        span {
          text-transform: uppercase;
        }
      </style>
      <katapult-button iconOnly noBorder icon="chevron_left" on-click="backAWeek"></katapult-button>
      <span>[[getDate(startDate)]] - [[getDate(endDate)]]</span>
      <katapult-button iconOnly noBorder icon="chevron_right" on-click="forwardAWeek"></katapult-button>
    `;
  }

  static get bodyTemplate() {
    return html`
      <paper-row raised-on-scroll grey>
        <paper-cell>Weekly Total</paper-cell>
        <paper-cell style="max-width:100px;" center>KP</paper-cell>
      </paper-row>
      <template is="dom-repeat" items="{{units}}">
        <paper-row>
          <paper-cell>
            <paper-input type="number" label="[[item.name]] ([[CamelCase(item.project)]])" value="{{item.quantity}}">
              <template is="dom-if" if="[[!item.price]]">
                <div slot="prefix" style="margin-right:5px">$</div>
              </template>
            </paper-input>
          </paper-cell>
          <paper-cell style="max-width:100px;" center>[[item.kp]]</paper-cell>
        </paper-row>
      </template>
      <paper-row style="margin:5px 0;">
        <paper-cell center>
          <katapult-button color="var(--secondary-color)" callback="save">Save</katapult-button>
        </paper-cell>
      </paper-row>
    `;
  }

  static get footNote() {
    return html`</template>`;
  }

  static get properties() {
    return {
      ikpRate: {
        type: Number,
        value: 89.06
      },
      startDate: {
        type: Object,
        computed: 'getStartDate(endDate)'
      },
      paperTableBody: {
        type: Boolean,
        value: true
      }
    };
  }

  static get observers() {
    return ['getBillableUnits(user.uid, projects.*)', 'updateKp(units.*)', 'getWeekData(startDate, userGroup, units)'];
  }

  ready() {
    super.ready();
    this.CamelCase = CamelCase;
    this.endDate = this.getKatapultEow(new Date());
  }

  getStartDate() {
    let startDate = new Date(this.endDate.getTime());
    startDate.setDate(startDate.getDate() - 7);
    return startDate;
  }

  getKatapultEow(d) {
    if (d.getTime() < 1699851600000) {
      //Prior to 12am on 9/13/2023 we treated Friday noon as the end of the week
      let newDate = this.getNextDayOfWeek(d, 5);
      newDate.setHours(12, 0, 0, 0);
      return newDate;
    } else {
      let newDate = this.getNextDayOfWeek(d, 1);
      newDate.setHours(0, 0, 0, 0);
      return newDate;
    }
  }
  // Gets the next occurance of dayOfWeek after date
  // If date is on dayOfWeek, returns date + 7 days,
  // Unless neverReturnDate == false, then return date
  getNextDayOfWeek(date, dayOfWeek = 0, neverReturnDate = true) {
    let out = new Date(date);
    if (neverReturnDate && out.getDay() === dayOfWeek) {
      out.setDate(date.getDate() + 7);
    } else {
      out.setDate(date.getDate() + ((7 + dayOfWeek - date.getDay()) % 7));
    }
    return out;
  }

  getDate(date) {
    return date.toLocaleString('default', { month: 'short' }) + ' ' + date.getDate();
  }

  backAWeek() {
    let endDate = new Date(this.endDate.getTime());
    endDate.setDate(endDate.getDate() - 12); // Subtracting 7 would get us into the previous week, but we subtract 12 to avoid any weekend issues since we changed the EOW from Friday to Monday
    this.endDate = this.getKatapultEow(endDate); // Get the EOW for the new date
  }

  forwardAWeek() {
    let endDate = new Date(this.endDate.getTime());
    endDate.setDate(endDate.getDate() + 5); //Adding 1 would get us into the next week, but we add 5 to avoid any weekend issues since we changed the EOW from Friday to Monday
    this.endDate = this.getKatapultEow(endDate); // Get the EOW for the new date
  }

  async getWeekData() {
    if (this.userGroup && this.startDate && this.units) {
      let s = await firebase
        .firestore()
        .collection(`companies/${this.userGroup}/billable_units`)
        .where('week_starting', '==', this.startDate)
        .get();
      let quantities = {};
      s.forEach((doc) => {
        let data = doc.data();
        quantities[data.project + ':' + data.unit_name] = data.quantity;
        let unitIndex = this.units.findIndex((unit) => unit.project == data.project && unit.name == data.unit_name);
        if (unitIndex != -1) {
        }
      });
      this.units.forEach((unit, i) => {
        this.set(`units.${i}.quantity`, quantities[unit.project + ':' + unit.name] ?? '');
      });
    }
  }

  getBillableUnits() {
    let units = [];
    for (let id in this.projects) {
      if (this.projects[id].users?.some((x) => x.uid == this.get('user.uid')) && this.projects[id].units) {
        let projectUnits = Path.copy(this.projects[id].units);
        projectUnits.forEach((unit, i) => {
          unit.project = id;
        });
        units.push(...projectUnits);
      }
    }
    this.units = units;
  }

  updateKp() {
    let weeklyValue = {
      Daily: 7,
      Weekly: 1,
      Monthly: 12 / 52,
      Yearly: 1 / 52
    };
    if (this.units) {
      this.units.forEach((unit, i) => {
        let kp = null;
        if (unit.quantity) {
          kp = Math.round((Number(unit.quantity) * Number(unit.price || 1) * (weeklyValue[unit.frequency] || 1)) / this.ikpRate);
        }
        this.set(`units.${i}.kp`, kp);
      });
    }
  }

  getDateString(date) {
    let parts = date
      .toLocaleDateString()
      .split('/')
      .map((x) => x.padStart(2, '0'));
    parts.unshift(parts.pop());
    return parts.join('-');
  }

  async save(e) {
    const button = e?.currentTarget;
    let ref = firebase.firestore().collection(`companies/${this.userGroup}/billable_units`);
    for (let unit of this.units) {
      let billableUnit = {
        uid: this.user.uid,
        week_starting: this.startDate,
        unit_name: unit.name,
        project: unit.project,
        price: unit.price ?? 1,
        quantity: unit.quantity
      };
      if (unit.frequency) billableUnit.frequency = unit.frequency;

      let s = await ref
        .where('week_starting', '==', billableUnit.week_starting)
        .where('unit_name', '==', billableUnit.unit_name)
        .where('project', '==', billableUnit.project)
        .get();
      if (s.empty) {
        ref.add(billableUnit);
      } else {
        s.docs[0].ref.update(billableUnit);
      }
    }
    button?.showSuccess();
  }
}
window.customElements.define(KatapultProjectBillableWidget.is, KatapultProjectBillableWidget);
